/**
 * Session Inactivity Manager for KvikSign
 * Automatically logs out authenticated users after 24 hours of inactivity.
 */

export const INACTIVITY_TIMEOUT_MS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
export const LAST_ACTIVITY_KEY = 'kviksign_last_activity';

let lastThrottleTime = 0;
const THROTTLE_INTERVAL_MS = 30 * 1000; // Throttle storage writes to once every 30 seconds

/**
 * Record user activity, throttled to prevent spamming localStorage.
 */
export function recordActivity(): void {
  if (typeof window === 'undefined') return;
  const now = Date.now();
  if (now - lastThrottleTime < THROTTLE_INTERVAL_MS) {
    return;
  }
  lastThrottleTime = now;
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
  } catch {
    // Gracefully handle private browsing or quota limits
  }
}

/**
 * Force-record current timestamp immediately (e.g. on login or manual touch).
 */
export function forceRecordActivity(): void {
  if (typeof window === 'undefined') return;
  const now = Date.now();
  lastThrottleTime = now;
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
  } catch {}
}

/**
 * Get the last recorded activity timestamp from localStorage.
 */
export function getLastActivity(): number | null {
  if (typeof window === 'undefined') return null;
  try {
    const val = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (!val) return null;
    const num = Number(val);
    return isNaN(num) || num <= 0 ? null : num;
  } catch {
    return null;
  }
}

/**
 * Checks whether the session has exceeded 24 hours of inactivity.
 */
export function isSessionExpired(): boolean {
  const last = getLastActivity();
  if (!last) return false;
  return Date.now() - last > INACTIVITY_TIMEOUT_MS;
}

/**
 * Clear the session activity timestamp (e.g. on logout or session expiration).
 */
export function clearSessionActivity(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  } catch {}
}
